import { BaseEntityModel } from '/@/modules/core/app/models/abstracts/BaseEntityModel';
import NumeralService from '/@/modules/core/app/services/NumeralService';
import { ColumnFormat } from '/@/modules/core/app/constants/data.constants';
import { FinancialAccountTransactionMeta } from '/@/modules/financialaccount/models/FinancialAccountTransactionMeta';
import { ModelType } from '/@/modules/core/app/constants/ModelType';
import { Asset } from '@/modules/asset/models/Asset';
import { Liability } from '@/modules/liability/models/Liability';
import { Note } from '@/modules/note/models/Note';
import { FinancialAccountTransactionCategory } from '@/modules/financialaccount/enums/FinancialAccountTransactionCategory';
import { EstateDistributionPayment } from '@/modules/estatedistribution/models/EstateDistributionPayment';
import { FinancialAccountTransactionPaymentChannel } from '@/modules/financialaccount/constants/FinancialAccountTransactionPaymentChannel';
import { FinancialAccountTransactionCategoryRelationshipKey } from '@/modules/accounting/types/FinancialAccountTransactionCategoryRelationshipKey';
import { FinancialAccountTransactionCategoryType } from '@/modules/financialaccount/constants/FinancialAccountTransactionCategoryType';

export class FinancialAccountTransaction extends BaseEntityModel {
  id?: number;
  modelId: number | null;
  modelType: string | null;
  estateId?: string;
  financialAccountId?: string;
  name?: string;
  type?: string;
  category: FinancialAccountTransactionCategory | null;
  isAutoReconciled?: boolean;
  merchantName?: string;
  paymentChannel?: FinancialAccountTransactionPaymentChannel;
  transactionCode?: string;
  transactionId?: string;
  pending?: string;
  amount: number | null;
  isManual?: boolean;
  date?: string;
  authorizedDate?: string;
  parentId?: string;
  isVirtual?: boolean;
  meta: FinancialAccountTransactionMeta;
  asset: Relationship<Asset> | null;
  liability: Relationship<Liability> | null;
  note: Relationship<Note> | null;
  estateDistributionPayment: Relationship<EstateDistributionPayment> | null;

  private original: Partial<FinancialAccountTransaction> | undefined;

  constructor(model: Partial<FinancialAccountTransaction> = {}) {
    super(model);

    this.id = model.id;
    this.modelId = model.modelId ?? null;
    this.modelType = model.modelType ?? null;
    this.estateId = model.estateId;
    this.financialAccountId = model.financialAccountId;
    this.name = model.name;
    this.type = model.type;
    this.category = model.category ?? null;
    this.isAutoReconciled = model.isAutoReconciled ?? false;
    this.merchantName = model.merchantName;
    this.paymentChannel = model.paymentChannel;
    this.transactionCode = model.transactionCode;
    this.transactionId = model.transactionId;
    this.pending = model.pending;
    this.amount = model.amount ?? null;
    this.isManual = model.isManual;
    this.date = model.date;
    this.authorizedDate = model.authorizedDate;
    this.parentId = model.parentId;
    this.isVirtual = model.isVirtual;
    this.meta = FinancialAccountTransactionMeta.mapModel(model.meta);
    Object.defineProperty(this, 'original', { enumerable: false });

    // @ts-ignore
    this.asset = Asset.mapModelOrNull(model.asset);
    // @ts-ignore
    this.liability = Liability.mapModelOrNull(model.liability);
    this.note = Note.mapModelOrNull(model.note);
    this.estateDistributionPayment = EstateDistributionPayment.mapModelOrNull(
      model.estateDistributionPayment
    );
  }

  getModelName(): string {
    return ModelType.FINANCIAL_ACCOUNT_TRANSACTION;
  }

  get displayValue(): string {
    return NumeralService.formatValue(this.amount, ColumnFormat.FORMAT_CURRENCY, 2);
  }

  setOriginal(): this {
    this.original = this.clone();

    return this;
  }

  updateFromTransaction(transaction: Partial<FinancialAccountTransaction>): this {
    Object.getOwnPropertyNames(this)
      .filter((key: string) => key !== 'original')
      .forEach((key: string) => (this[key] = transaction[key]));

    return this;
  }

  resetFromOriginal(): this {
    this.updateFromTransaction(this.original!);

    return this;
  }

  get isDirty(): boolean {
    return JSON.stringify(this.original) !== JSON.stringify(this);
  }

  getOriginalValue(key: keyof FinancialAccountTransaction): unknown {
    return this.original?.[key];
  }

  updateRelationshipsOnCategoryChange(): void {
    switch (this.categoryRelationshipKey) {
      case ModelType.ASSET:
        this.liability = null;
        this.estateDistributionPayment = null;
        if (this.modelType !== ModelType.ASSET) {
          this.modelId = null;
          this.modelType = null;
        }
        break;
      case ModelType.LIABILITY:
        this.asset = null;
        this.estateDistributionPayment = null;
        if (this.modelType !== ModelType.LIABILITY) {
          this.modelId = null;
          this.modelType = null;
        }
        break;
      case ModelType.ESTATE_ACCOUNT:
        this.asset = null;
        this.liability = null;
        this.modelType = null;
        this.modelId = null;
        break;
      default:
        // since a category cannot be set to null once it's set, we don't need to do anything here
        break;
    }
  }

  updateEstateDistributionPaymentOnAmountChange(): void {
    if (this.estateDistributionPayment) {
      this.estateDistributionPayment.amountPaid = Math.abs(this.amount!);
    }
  }

  get hasRelationshipEntity(): boolean {
    switch (this.categoryRelationshipKey) {
      case ModelType.ASSET:
        return !!this.asset;
      case ModelType.LIABILITY:
        return !!this.liability;
      case ModelType.ESTATE_ACCOUNT:
        return !!this.estateDistributionPayment?.estateAccount;
      default:
        return false;
    }
  }

  get canBeSaved(): boolean {
    return (
      !!this.date &&
      !!this.name &&
      this.amount !== null &&
      (!this.categoryType.requiresRelationship || this.hasRelationshipEntity)
    );
  }

  get categoryRelationshipKey(): FinancialAccountTransactionCategoryRelationshipKey {
    return this.categoryType.categoryRelationshipKey;
  }

  get categoryType(): FinancialAccountTransactionCategoryType {
    return FinancialAccountTransactionCategoryType.getByKey(this.category)!;
  }

  isLinkedToEntity(modelType: string, modelId: number): boolean {
    return this.modelType === modelType && this.modelId === modelId;
  }

  canLinkToEntity(modelType: string): boolean {
    if (!this.amount) {
      return false;
    }

    return modelType === ModelType.ASSET ? this.amount > 0 : this.amount < 0;
  }

  get shortenedName(): string {
    if (!this.name) {
      return '';
    }

    return this.name.length > 20 ? this.name.substring(0, 20) + '...' : this.name;
  }
}
